import React, { useState } from 'react';
import { CardGrid, ContentSection, LinkRow, PageFrame, PageHero } from '../components/PageBlocks';
import { getPageConfig } from '../data/page-config.mjs';

const STRIPE_URL_AVENBOX = import.meta.env.VITE_STRIPE_CHECKOUT_URL_SOVEREIGN_PRO as string;

const page = getPageConfig('/contact');

const contactPaths = [
    {
        title: 'General inquiry',
        body: 'Product questions, company questions, and any practical follow-up.',
        email: 'hello@avenstudios.com',
    },
    {
        title: 'Press inquiry',
        body: 'Interviews, background requests, and media context. All handled directly by the team.',
        email: 'hello@avenstudios.com',
    },
    {
        title: 'Investor inquiry',
        body: 'Serious company, financing, and strategic conversation at the current stage.',
        email: 'hello@avenstudios.com',
    },
];

const ContactPage: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');

        if (!name.trim() || !email.trim()) {
            setError('Please fill in all fields.');
            return;
        }

        if (name.trim().length > 100) {
            setError('Name is too long (max 100 characters).');
            return;
        }

        if (email.trim().length > 254) {
            setError('Email is too long.');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setLoading(true);

        // client_reference_id only accepts [\w-]{1,200}. Base64url-encode the name
        // and prefix with tier so the webhook can insert the row after payment.
        const nameB64 = btoa(unescape(encodeURIComponent(name.trim())))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        const ref = `avenbox-${nameB64}`.slice(0, 200);
        window.location.href = `${STRIPE_URL_AVENBOX}?prefilled_email=${encodeURIComponent(email.trim())}&client_reference_id=${ref}`;
    };

    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="rail"
                numeral="IX"
                railLabel="Aven / Direct Contact / 09"
                coord="hello@avenstudios.com"
                eyebrow="Contact"
                title={page.h1}
                intro="Three direct paths: general inquiry, press, and investor. All route to the team without intermediary layers. The reservation form below is for the €1 early-access deposit."
                proofStrip={[
                    'hello@avenstudios.com',
                    'General, press, and investor inquiries handled directly.',
                    'Reservation path is separate from general contact.',
                ]}
            />

            <ContentSection
                sectionId="direct-contact"
                eyebrow="Direct Contact"
                title="Three clear paths to the team"
                intro="All inquiry types currently route through one inbox, handled directly at this stage."
                borderTop={false}
            >
                <CardGrid
                    pattern="meta"
                    items={contactPaths.map((path) => ({
                        title: path.title,
                        body: `${path.body} — ${path.email}`,
                    }))}
                />
                <p className="contact-direct-line">
                    Direct email: <a href="mailto:hello@avenstudios.com">hello@avenstudios.com</a>
                </p>
            </ContentSection>

            <ContentSection
                sectionId="early-access"
                eyebrow="Early Access"
                title="Reserve a position with €1"
                intro="The €1 deposit places you in a controlled early-access position ahead of a confirmed production run. It is not a purchase of a finished product. Pricing and specifications are directional at this stage."
            >
                <div className="form-panel">
                    <form onSubmit={handleSubmit} className="form-stack">
                        <div>
                            <label className="field-label" htmlFor="contact-name">
                                Full name
                            </label>
                            <input
                                id="contact-name"
                                type="text"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                placeholder="Your name"
                                maxLength={100}
                                className="field-input"
                            />
                        </div>

                        <div>
                            <label className="field-label" htmlFor="contact-email">
                                Email
                            </label>
                            <input
                                id="contact-email"
                                type="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                placeholder="you@example.com"
                                maxLength={254}
                                className="field-input"
                            />
                        </div>

                        <div className="choice-summary">
                            <span className="choice-summary__title">AvenBox</span>
                            <span className="choice-summary__meta">
                                EUR 1,199 — directional pricing, single configuration
                            </span>
                        </div>

                        {error ? (
                            <p className="text-body" style={{ color: '#f87171' }}>
                                {error}
                            </p>
                        ) : null}

                        <button type="submit" disabled={loading} className="btn-pill btn-primary form-submit">
                            {loading ? 'Redirecting to checkout...' : 'Continue to €1 checkout'}
                        </button>

                        <p className="status-note">
                            The €1 deposit records early-access interest in the selected track.
                            It does not lock final specifications, final pricing, or delivery timing.
                        </p>
                    </form>
                </div>
            </ContentSection>

            <ContentSection sectionId="context" eyebrow="Context" title="Read the supporting pages first">
                <LinkRow
                    links={[
                        { label: 'Pricing', to: '/pricing' },
                        { label: 'FAQ', to: '/faq' },
                        { label: 'About', to: '/about' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default ContactPage;
